import {
  isMultileg,
  useConnectionStatusContext,
  useMarketAccountsContext,
  useMarketsContext,
  useSecuritiesContext,
  useUnifiedLiquidityContext,
  type Security,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { usePermittedTradableMarketAccountsSet } from '../../hooks/usePermittedTradableMarketAccountsSet';
import { useSubAccountAllocationsForRestrictedTrading } from '../../hooks/useSubAccountAllocationsForRestrictedTrading';
import { useDefaultAggregation } from '../../providers/DefaultAggregationContext';
import { MULTILEG_MARKET_ACCOUNT } from '../../providers/OMSContext.types';
import { useTradingSettings } from '../../providers/TradingSettingsContext';
import { useMarketSelectorAggregationPresets } from '../MarketSelector/presets/useMarketSelectorAggregationPresets';
import {
  buildTradingSymbolDefaultPanelMarkets,
  getTradingSymbolMarketSelectorItems,
} from '../MarketSelector/tradingSymbolDefaultMarkets';
import { isMarketSelectorItemSelectable } from '../MarketSelector/utils/items';

export interface MarketDataCardSymbolChangeSelections {
  security: Security;
  selectedAggregation: string | undefined;
  selectedMarkets: string[];
  selectedMarketAccounts: string[];
}

export function useMarketDataCardSymbolChangeSelections() {
  const { useNewMarketSelector } = useTradingSettings();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { marketAccountsByMarket, marketAccountsByName } = useMarketAccountsContext();
  const { marketsByName } = useMarketsContext();
  const { unifiedLiquidityTokenBySymbol } = useUnifiedLiquidityContext();
  const { marketCredentialsWithConnectionStatusByMarketAccountName } = useConnectionStatusContext();
  const { defaultAggregationId } = useDefaultAggregation();
  const { aggregationPresets } = useMarketSelectorAggregationPresets();
  const { enableAccountSegregationWithRestrictedTrading } = useFeatureFlag();
  const subAccountAllocations = useSubAccountAllocationsForRestrictedTrading();
  const permittedMarketAccountNames = usePermittedTradableMarketAccountsSet({
    subAccountAllocations,
    combineAllMarketAccounts: enableAccountSegregationWithRestrictedTrading,
  });

  return useCallback(
    (
      symbol: string | undefined,
      includeUnifiedLiquidityMarkets: boolean
    ): MarketDataCardSymbolChangeSelections | undefined => {
      const security = symbol ? securitiesBySymbol.get(symbol) : undefined;
      if (!security) {
        return undefined;
      }

      if (isMultileg(security)) {
        return {
          security,
          selectedMarketAccounts: [MULTILEG_MARKET_ACCOUNT],
          selectedMarkets: [],
          selectedAggregation: undefined,
        };
      }

      const tradingSymbolCtx = {
        securitiesBySymbol,
        marketAccountsByMarket,
        marketsByName,
        marketAccountsByName,
        unifiedLiquidityTokenBySymbol,
        enableAccountSegregationWithRestrictedTrading,
        permittedMarketAccountNames: enableAccountSegregationWithRestrictedTrading ? permittedMarketAccountNames : null,
        marketCredentialsWithConnectionStatusByMarketAccountName,
      };
      const items = getTradingSymbolMarketSelectorItems(
        security,
        security.Symbol,
        includeUnifiedLiquidityMarkets,
        tradingSymbolCtx
      );
      const selectableItems = items.filter(isMarketSelectorItemSelectable);

      if (selectableItems.length === 1) {
        const item = selectableItems[0];
        return {
          security,
          selectedMarketAccounts: item.marketAccount ? [item.marketAccount.Name] : [],
          selectedMarkets: item.marketAccount ? [] : [item.market.Name],
          selectedAggregation: undefined,
        };
      }

      const fromDefault = buildTradingSymbolDefaultPanelMarkets({
        ...tradingSymbolCtx,
        nextSymbol: security.Symbol,
        security,
        useNewMarketSelector,
        defaultAggregationId,
        aggregationPresets,
        includeUnifiedLiquidityMarkets,
        marketSelectorItems: items,
      });

      if (fromDefault.aggregation != null || fromDefault.markets.length > 0 || fromDefault.marketAccounts.length > 0) {
        return {
          security,
          selectedMarkets: fromDefault.markets,
          selectedMarketAccounts: fromDefault.marketAccounts,
          selectedAggregation: fromDefault.aggregation,
        };
      }

      return {
        security,
        selectedMarketAccounts: [],
        selectedMarkets: [],
        selectedAggregation: undefined,
      };
    },
    [
      aggregationPresets,
      defaultAggregationId,
      enableAccountSegregationWithRestrictedTrading,
      marketAccountsByMarket,
      marketAccountsByName,
      marketCredentialsWithConnectionStatusByMarketAccountName,
      marketsByName,
      permittedMarketAccountNames,
      securitiesBySymbol,
      unifiedLiquidityTokenBySymbol,
      useNewMarketSelector,
    ]
  );
}
